import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { casesApi } from './caseApi';
import { UserContext } from '../../context/UserContext'; // Import UserContext
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog'; // Import DeleteConfirmationDialog
import PropTypes from 'prop-types'; // Import PropTypes

const CasesContext = createContext(null);

const DEFAULT_LIMIT = 12; // Default items per page

// Accept searchTerm, selectedTags, and selectedHospital as props
export const CasesContextProvider = ({ children, searchTerm = '', selectedTags = [], selectedHospital = null, selectedDepartment = null }) => {
  const { user } = useContext(UserContext); // Get user for favorite status check
  const [cases, setCases] = useState([]); // Holds the 'reports' array from API
  const [isLoading, setIsLoading] = useState(true); // Renamed from loading
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid | list

  // --- Filter State (Remove search, tags are passed as props) ---
  const [filter, setFilter] = useState({
    // Add other potential filters like hospital, department etc. if needed
  });

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  // --- Tags State ---
  const [allTags, setAllTags] = useState([]); // State to hold all available tags {id, name}

  // --- Delete Confirmation State ---
  const [isConfirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [caseForConfirmation, setCaseForConfirmation] = useState(null); // Store the full case object for confirmation

  // --- Toast Notification State ---
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' }); // Added type

  // Function to show toast and auto-hide
  const showToast = (message, type = 'info', duration = 3000) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'info' });
    }, duration);
  };

  // --- Data Fetching ---
  const fetchAllTags = useCallback(async () => {
    // setIsLoading(true); // Optionally set loading for tags fetch as well
    try {
      const tagsData = await casesApi.getAllTags();
      setAllTags(tagsData); // Store { id, name } objects
    } catch (error) {
      console.error('Error fetching all tags for context:', error);
      // Optionally show a toast or set a specific error state
      // showToast('שגיאה בטעינת רשימת התגיות', 'error');
    } finally {
      // setIsLoading(false); // Ensure loading is false if set true at start
    }
  }, []); // Removed setIsLoading from dependencies, as it's a setter

  const fetchCases = useCallback(async () => {
    setIsLoading(true); // Renamed from setLoading
    setError(null);
    try {
      // Prepare filters for API call using props
      const apiFilters = {
        searchTerm: searchTerm || undefined, // Use searchTerm prop
        tags: selectedTags.length > 0 ? selectedTags.map(t => t.name).join(',') : undefined, // Use selectedTags prop
        hospital: selectedHospital || undefined, // Use selectedHospital prop
        department: selectedDepartment || undefined,
        // Add other filters from internal state if needed: ...filter
      };

      const pagination = { page: currentPage, limit };

      // Pass the correct filter object to the API
      const data = await casesApi.list(apiFilters, pagination);

      // Use actual API response fields: reports, total, totalPages, page, limit
      setCases(data.cases || []); // Changed from data.docs
      setTotalDocs(data.total || 0); // Changed from data.totalDocs
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.page || 1); // Ensure current page is updated from response
      setLimit(data.limit || DEFAULT_LIMIT);

    } catch (err) {
      console.error('Error loading cases:', err);
      setError('אירעה שגיאה בטעינת המקרים');
      showToast('אירעה שגיאה בטעינת המקרים', 'error');
    } finally {
      setIsLoading(false); // Renamed from setLoading
    }
    // Update dependencies: fetchCases should re-run when filters or pagination change
  }, [searchTerm, selectedTags, selectedHospital, selectedDepartment, currentPage, limit, setIsLoading]); // Added setIsLoading

  // Function to manually trigger a refetch
  const refreshCases = useCallback(() => {
    // Resetting to page 1 might be desired on manual refresh, or keep current page
    // setCurrentPage(1); // Optional: uncomment if refresh should always go to first page
    fetchCases();
    fetchAllTags(); // Also refresh the list of available tags
  }, [fetchCases, fetchAllTags]); // Add fetchAllTags dependency

  // Initial fetch and refetch on filter/page change
  useEffect(() => {
    fetchCases();
    fetchAllTags(); // Fetch tags on initial load as well
  }, [fetchCases, fetchAllTags]); // Add fetchAllTags dependency

  // --- Filter Update (Only needed if internal filters exist beyond search/tags) ---
  // const updateFilter = (newState) => {
  //   // Reset to page 1 when filters change
  //   setCurrentPage(1);
  //   setFilter((prev) => ({ ...prev, ...newState }));
  // };

  // --- Delete Action ---
  const requestDeleteCase = (caseItem) => {
    // Store the full case object for the confirmation dialog
    setCaseForConfirmation(caseItem);
    setConfirmDeleteDialogOpen(true);
  };

  // Renamed from confirmDelete, this is specifically for the context's dialog
  const handleConfirmDeleteFromContextDialog = async () => { 
    if (!caseForConfirmation) return;
    const caseIdToDelete = caseForConfirmation._id;
    const caseNameToDelete = caseForConfirmation.name;

    try {
      await casesApi.remove(caseIdToDelete);
      showToast(`מקרה "${caseNameToDelete}" נמחק בהצלחה`, 'success');
      fetchCases(); // Refetch data
    } catch (err) {
      console.error('Error deleting case from context dialog:', err);
      showToast(`שגיאה במחיקת מקרה "${caseNameToDelete}"`, 'error');
      setError('אירעה שגיאה במחיקת המקרה');
    } finally {
      setConfirmDeleteDialogOpen(false);
      setCaseForConfirmation(null);
    }
  };

  const cancelDelete = () => { // This is for the context's dialog
    setConfirmDeleteDialogOpen(false);
    setCaseForConfirmation(null);
  };

  // New function for direct deletion, typically called from CaseView
  const deleteCaseAndHandleSuccess = async (caseItem, onSuccessCallback) => {
    if (!caseItem) return;
    const caseIdToDelete = caseItem._id;
    const caseNameToDelete = caseItem.name;

    try {
      await casesApi.remove(caseIdToDelete);
      showToast(`מקרה "${caseNameToDelete}" נמחק בהצלחה`, 'success');
      fetchCases(); // Refetch data
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    } catch (err) {
      console.error('Error deleting case directly:', err);
      showToast(`שגיאה במחיקת מקרה "${caseNameToDelete}"`, 'error');
      setError('אירעה שגיאה במחיקת המקרה');
      // Optionally, call an onErrorCallback if provided
    }
  };

  // --- Favorite Action ---
  const toggleFavorite = async (caseItem) => {
    if (!user) {
      showToast('יש להתחבר כדי להוסיף למועדפים', 'warning');
      return;
    }

    const caseId = caseItem._id;
    const isCurrentlyFavorite = caseItem.favoritedBy?.includes(user._id);

    // Optimistic UI update (optional but good for UX)
    setCases(prevCases =>
      prevCases.map(c =>
        c._id === caseId
          ? {
              ...c,
              favoritedBy: isCurrentlyFavorite
                ? c.favoritedBy.filter(id => id !== user._id)
                : [...(c.favoritedBy || []), user._id],
            }
          : c
      )
    );

    try {
      if (isCurrentlyFavorite) {
        await casesApi.unfavorite(caseId);
        showToast(`"${caseItem.name}" הוסר מהמועדפים`, 'success');
      } else {
        await casesApi.favorite(caseId);
        showToast(`"${caseItem.name}" הוסף למועדפים`, 'success');
      }
      // Optionally refetch to ensure consistency, or rely on optimistic update
      // fetchCases();
    } catch (err) {
      console.error('Error toggling favorite:', err);
      showToast(`שגיאה בעדכון מועדפים עבור "${caseItem.name}"`, 'error');
      // Revert optimistic update on error
      setCases(prevCases =>
        prevCases.map(c =>
          c._id === caseId
            ? {
                ...c,
                favoritedBy: isCurrentlyFavorite
                  ? [...(c.favoritedBy || []), user._id] // Add back if unfavorite failed
                  : c.favoritedBy.filter(id => id !== user._id), // Remove if favorite failed
              }
            : c
        )
      );
    }
  };

  // --- Context Value ---
  const value = {
    cases, // The actual list of medical cases (docs)
    isLoading, // Renamed from loading
    error,
    viewMode,
    // filter, // Expose internal filter state only if needed
    currentPage,
    totalPages,
    totalDocs,
    limit,
    // updateFilter, // Expose only if needed
    setViewMode,
    setCurrentPage, // Function to change page
    setLimit,       // Function to change items per page (optional)
    requestDeleteCase, 
    toggleFavorite,
    refreshCases, // Expose the refresh function
    allTags, // Expose the fetched tags
    deleteCaseAndHandleSuccess, // Expose the new delete function
  };

  return (
    <CasesContext.Provider value={value}>
      {children}

      {/* Toast notification */}
      {toast.show && (
         <div className={`toast-notification toast-${toast.type}`}>{toast.message}</div>
      )}

      {/* Delete confirmation modal using DeleteConfirmationDialog */}
      <DeleteConfirmationDialog
        open={isConfirmDeleteDialogOpen}
        title={caseForConfirmation ? `האם אתה בטוח שברצונך למחוק את המקרה "${caseForConfirmation.name}"?` : ''}
        onConfirm={handleConfirmDeleteFromContextDialog} // Use renamed handler
        onClose={cancelDelete}
      />
    </CasesContext.Provider>
  );
};

// Add PropTypes for the new props
CasesContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  searchTerm: PropTypes.string,
  selectedTags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.any.isRequired, // Or specific type like number/string
    name: PropTypes.string.isRequired,
  })),
  selectedHospital: PropTypes.string, // Can be string (ID) or null/undefined
  selectedDepartment: PropTypes.string,
};


// Export the hook with a more explicit error message
export const useCases = () => {
  const context = useContext(CasesContext);
  if (context === null) { // Check for null, the initial value
    throw new Error('useCases must be used within a CasesContextProvider');
  }
  return context;
};

// Export the context for direct use if needed
export default CasesContext;
